import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  UserGroupIcon,
  ExclamationTriangleIcon,
  UserIcon,
  StarIcon,
  TrophyIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  FunnelIcon,
  ArrowsUpDownIcon
} from '@heroicons/react/24/outline';
import { TeamService, UserService, PredictionService, SeasonService, ContestantService } from '../../services/firebaseService';
import { formatDate } from '../../utils/dateUtils';
import { calculateRanks } from '../../utils/rankingUtils';
import { getContestantById, normalizeToContestantId } from '../../utils/contestantUtils';
import { useAuth } from '../../contexts/AuthContext';
import { hasUserClaimedTeam } from '../../utils/teamUtils';
import { isMultiEliminationWeek } from '../../utils/predictionUtils';

/**
 * Reusable component for displaying teams in a league season
 * Can be used by both admin and league manager views
 */
export default function SeasonTeamsList({ 
  leagueId, 
  seasonId, 
  season, 
  league, 
  showActions = false,
  onViewTeam,
  onEditTeam,
  onRemoveTeam,
  onAddTeam,
  hideMembers = false,
  hideSubmissionStatus = false,
  showTeamLinks = false,
  hideFilters = false
}) {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [claimingStatus, setClaimingStatus] = useState({});
  const [claimingDetails, setClaimingDetails] = useState({}); // Lazy-loaded details
  const [claimingDetailsLoading, setClaimingDetailsLoading] = useState({});
  const [submissionStatus, setSubmissionStatus] = useState({}); // Team submission stats
  const [filter, setFilter] = useState('all'); // 'all', 'claimed', 'unclaimed'
  const [sortBy, setSortBy] = useState('score'); // 'score', 'claiming'
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [contestants, setContestants] = useState([]);

  useEffect(() => {
    if (leagueId && seasonId) {
      loadTeamsData();
    }
  }, [leagueId, seasonId]);

  const loadTeamsData = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Always fetch teams data
      const teamsData = await TeamService.getLeagueSeasonTeams(leagueId, seasonId);
      setTeams(teamsData || []);
      
      // Load claiming status for all teams (batch) - do this after teams are set
      if (teamsData && teamsData.length > 0) {
        const teamIds = teamsData.map(team => team.id);
        try {
          const statusMap = await TeamService.getTeamsClaimingStatus(teamIds, leagueId, seasonId);
          setClaimingStatus(statusMap);
        } catch (claimingError) {
          console.warn('Could not fetch claiming status:', claimingError);
          setClaimingStatus({});
        }
      } else {
        setClaimingStatus({});
      }
      
      // Try to fetch users data, but don't fail if we don't have permission
      try {
        const allUsers = await UserService.getAllUsers();
        setUsers(allUsers);
      } catch (userError) {
        console.warn('Could not fetch users data (insufficient permissions):', userError);
        // Set empty users array - we'll show user IDs instead of names
        setUsers([]);
      }

      // Load submission status for all teams
      if (teamsData && teamsData.length > 0) {
        try {
          // Load season data to get weeks
          const seasonData = await SeasonService.getSeason(seasonId);
          const weeksList = seasonData?.weeks || [];
          if (weeksList.length > 0) {
            await loadSubmissionStatus(leagueId, seasonId, teamsData, weeksList);
          }
        } catch (submissionError) {
          console.warn('Could not fetch submission status:', submissionError);
          setSubmissionStatus({});
        }
      }

      // Load contestants for draft pick status
      try {
        const contestantsData = await ContestantService.getContestantsBySeason(seasonId);
        setContestants(contestantsData);
      } catch (contestantError) {
        console.warn('Could not fetch contestants:', contestantError);
        setContestants([]);
      }
    } catch (error) {
      console.error('Error loading teams data:', error);
      setError('Failed to load teams data');
    } finally {
      setLoading(false);
    }
  };

  const loadSubmissionStatus = async (leagueId, seasonId, teamsList, weeksList) => {
    try {
      // Get all predictions for this league/season
      const allPredictions = await PredictionService.getAllLeaguePredictions(leagueId, seasonId);
      
      // Group predictions by team and week
      const predictionsByTeam = {};
      allPredictions.forEach(prediction => {
        const teamId = prediction.teamId;
        if (!predictionsByTeam[teamId]) {
          predictionsByTeam[teamId] = {};
        }
        const weekNum = prediction.weekNumber;
        if (!predictionsByTeam[teamId][weekNum]) {
          predictionsByTeam[teamId][weekNum] = [];
        }
        predictionsByTeam[teamId][weekNum].push(prediction);
      });

      // Calculate submission stats for each team
      const stats = {};
      teamsList.forEach(team => {
        const teamPredictions = predictionsByTeam[team.id] || {};
        const weeksWithPredictions = Object.keys(teamPredictions).length;
        const totalWeeks = weeksList.length;
        const weeksWithoutPredictions = totalWeeks - weeksWithPredictions;
        const completionRate = totalWeeks > 0 ? Math.round((weeksWithPredictions / totalWeeks) * 100) : 0;
        
        // Find last prediction date
        let lastPredictionDate = null;
        allPredictions
          .filter(p => p.teamId === team.id)
          .forEach(prediction => {
            if (prediction.timestamp || prediction.createdAt || prediction.updatedAt) {
              const predDate = prediction.timestamp?.toDate 
                ? prediction.timestamp.toDate() 
                : (prediction.createdAt?.toDate 
                  ? prediction.createdAt.toDate() 
                  : (prediction.updatedAt?.toDate 
                    ? prediction.updatedAt.toDate() 
                    : new Date(prediction.timestamp || prediction.createdAt || prediction.updatedAt)));
              if (!lastPredictionDate || predDate > lastPredictionDate) {
                lastPredictionDate = predDate;
              }
            }
          });

        stats[team.id] = {
          weeksWithPredictions,
          weeksWithoutPredictions,
          totalWeeks,
          completionRate,
          lastPredictionDate
        };
      });

      setSubmissionStatus(stats);
    } catch (error) {
      console.error('Error loading submission status:', error);
      throw error;
    }
  };

  const loadClaimingDetails = async (teamId) => {
    if (claimingDetails[teamId] || claimingDetailsLoading[teamId]) {
      return; // Already loaded or loading
    }

    try {
      setClaimingDetailsLoading(prev => ({ ...prev, [teamId]: true }));
      const usersList = await UserService.getUsersForTeam(teamId, leagueId, seasonId);
      
      // Get user details with claim timestamps
      const details = usersList.map((user) => {
        // Get the claim timestamp from user's teams data
        let claimedAt = null;
        
        const teams = user.teams || {};
        const leagueData = teams[leagueId];
        if (leagueData) {
          const seasonData = leagueData[seasonId];
          if (seasonData) {
            // Handle array format (multiple teams per league-season)
            if (Array.isArray(seasonData)) {
              const teamEntry = seasonData.find(teamValue => {
                const teamIdValue = typeof teamValue === 'string' 
                  ? teamValue 
                  : (teamValue?.teamId || null);
                return teamIdValue === teamId;
              });
              if (teamEntry && typeof teamEntry === 'object' && teamEntry.claimedAt) {
                claimedAt = teamEntry.claimedAt;
              }
            } else if (typeof seasonData === 'object' && seasonData.claimedAt) {
              // Handle single team format with claimedAt
              claimedAt = seasonData.claimedAt;
            }
          }
        }
        
        return {
          id: user.id,
          email: user.email || user.displayName || `User ${user.id.substring(0, 8)}...`,
          displayName: user.displayName || user.email || `User ${user.id.substring(0, 8)}...`,
          claimedAt
        };
      });
      
      setClaimingDetails(prev => ({ ...prev, [teamId]: details }));
    } catch (error) {
      console.error(`Error loading claiming details for team ${teamId}:`, error);
      setClaimingDetails(prev => ({ ...prev, [teamId]: [] }));
    } finally {
      setClaimingDetailsLoading(prev => {
        const newState = { ...prev };
        delete newState[teamId];
        return newState;
      });
    }
  };

  const toggleRowExpansion = (teamId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(teamId)) {
      newExpanded.delete(teamId);
    } else {
      newExpanded.add(teamId);
      // Load details when expanding
      loadClaimingDetails(teamId);
    }
    setExpandedRows(newExpanded);
  };

  const formatClaimDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      if (timestamp.seconds) {
        return formatDate({ seconds: timestamp.seconds });
      } else if (timestamp.toDate) {
        return formatDate(timestamp.toDate());
      } else {
        return formatDate(new Date(timestamp));
      }
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const getUserById = (userId) => {
    return users.find(user => user.id === userId);
  };

  const getUserDisplayName = (userId) => {
    const user = getUserById(userId);
    return user ? user.displayName : `User ${userId.substring(0, 8)}...`;
  };

  const getTeamScore = (team) => {
    // Use currentScore if available, otherwise calculate from weeklyScores
    if (team.currentScore !== undefined) {
      return team.currentScore;
    }
    
    // Fallback: calculate total score from all weeks
    if (!team.weeklyScores || !season?.weeks) return 0;
    
    return season.weeks.reduce((total, week) => {
      const weekScore = team.weeklyScores[week.weekNumber] || 0;
      return total + weekScore;
    }, 0);
  };

  /**
   * Check if a contestant has been eliminated
   * @param {string} contestantId - Contestant ID
   * @returns {boolean} True if eliminated
   */
  const isContestantEliminated = (contestantId) => {
    if (!season || !season.weeks || !contestantId || !contestants || contestants.length === 0) {
      return false;
    }
    
    const normalizedContestantId = normalizeToContestantId(contestantId, contestants);
    if (!normalizedContestantId) return false;
    
    const contestant = getContestantById(normalizedContestantId, contestants);
    if (!contestant) return false;
    
    const contestantIdToMatch = contestant.id;
    const contestantNameToMatch = contestant.name;
    
    // Check all weeks for eliminations
    for (const weekData of season.weeks) {
      if (!weekData.results || Object.keys(weekData.results).length === 0) continue;
      
      const results = weekData.results;
      
      // Helper function to check if a result value matches our contestant
      const matchesContestant = (resultValue) => {
        if (!resultValue) return false;
        const resultId = normalizeToContestantId(resultValue, contestants);
        if (resultId === contestantIdToMatch) return true;
        const resultContestant = getContestantById(resultValue, contestants) || 
                                 contestants.find(c => c.name === resultValue);
        if (resultContestant) {
          return resultContestant.id === contestantIdToMatch || 
                 resultContestant.name === contestantNameToMatch;
        }
        return resultValue === contestantIdToMatch || resultValue === contestantNameToMatch;
      };
      
      // Check for finale eliminations
      if (weekData.isFinale) {
        if (results.votedOut && matchesContestant(results.votedOut)) return true;
        if (results.fireMakingLoser && matchesContestant(results.fireMakingLoser)) return true;
      }
      else if (
        isMultiEliminationWeek(weekData) &&
        Array.isArray(results.votedOut)
      ) {
        for (const result of results.votedOut) {
          const resultValue = result.contestantId || result.contestantName || result;
          if (matchesContestant(resultValue)) return true;
        }
      }
      // Check for single elimination
      else if (results.votedOut) {
        if (matchesContestant(results.votedOut)) return true;
      }
    }
    
    return false;
  };

  const getContestantName = (contestantIdOrName) => {
    if (!contestantIdOrName) return 'N/A';
    const contestant = getContestantById(contestantIdOrName, contestants);
    return contestant ? contestant.name : contestantIdOrName;
  };

  // Calculate ranks using the utility function
  const rankedTeams = useMemo(() => {
    return calculateRanks(teams, getTeamScore);
  }, [teams]);

  const getTeamRank = (team) => {
    const rankedTeam = rankedTeams.find(t => t.id === team.id);
    return rankedTeam ? rankedTeam.rank : teams.length;
  };

  const getSubmissionStatusColor = (completionRate) => {
    if (completionRate >= 75) return 'bg-green-100 text-green-800';
    if (completionRate >= 50) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  const getSubmissionStatusLabel = (completionRate) => {
    if (completionRate >= 75) return 'Good';
    if (completionRate >= 50) return 'Fair';
    return 'Low';
  };

  // Filter and sort teams - MUST be before early returns
  const filteredAndSortedTeams = useMemo(() => {
    let filtered = teams.map(team => {
      const teamScore = getTeamScore(team);
      const status = claimingStatus[team.id] || { isClaimed: false, claimCount: 0 };
      
      return {
        ...team,
        score: teamScore,
        isClaimed: status.isClaimed,
        claimCount: status.claimCount
      };
    });

    // Apply filter
    if (filter === 'claimed') {
      filtered = filtered.filter(team => team.isClaimed);
    } else if (filter === 'unclaimed') {
      filtered = filtered.filter(team => !team.isClaimed);
    }

    // Apply sort
    if (sortBy === 'claiming') {
      filtered.sort((a, b) => {
        // Claimed teams first, then by claim count
        if (a.isClaimed !== b.isClaimed) {
          return b.isClaimed ? 1 : -1;
        }
        return b.claimCount - a.claimCount;
      });
    } else {
      // Default: sort by score
      filtered.sort((a, b) => b.score - a.score);
    }

    return filtered;
  }, [teams, claimingStatus, filter, sortBy]);

  // Early returns AFTER all hooks
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-survivor-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading teams...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Teams</h3>
        <p className="text-gray-600 mb-4">{error}</p>
        <button
          onClick={loadTeamsData}
          className="px-4 py-2 bg-survivor-600 text-white rounded-md hover:bg-survivor-700"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <UserGroupIcon className="h-6 w-6 text-gray-400 mr-2" />
            <h2 className="text-lg font-medium text-gray-900">Teams Leaderboard</h2>
            <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
              {filteredAndSortedTeams.length} {filter !== 'all' && `of ${teams.length}`}
            </span>
          </div>
          {showActions && onAddTeam && (
            <button
              onClick={onAddTeam}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-survivor-600 hover:bg-survivor-700"
            >
              Add Team
            </button>
          )}
        </div>
        
        {/* Filters and Sort */}
        {!hideFilters && (
          <div className="flex items-center space-x-3 flex-wrap gap-2">
            <div className="flex items-center space-x-2">
              <FunnelIcon className="h-4 w-4 text-gray-400" />
              <span className="text-sm text-gray-600">Filter:</span>
              <button
                onClick={() => setFilter('all')}
                className={`px-3 py-1 text-sm font-medium rounded-md ${
                  filter === 'all'
                    ? 'bg-survivor-100 text-survivor-700 border border-survivor-300'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setFilter('claimed')}
                className={`px-3 py-1 text-sm font-medium rounded-md ${
                  filter === 'claimed'
                    ? 'bg-green-100 text-green-700 border border-green-300'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                Claimed
              </button>
              <button
                onClick={() => setFilter('unclaimed')}
                className={`px-3 py-1 text-sm font-medium rounded-md ${
                  filter === 'unclaimed'
                    ? 'bg-red-100 text-red-700 border border-red-300'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                Unclaimed
              </button>
            </div>
            
            <div className="flex items-center space-x-2">
              <ArrowsUpDownIcon className="h-4 w-4 text-gray-400" />
              <span className="text-sm text-gray-600">Sort:</span>
              <button
                onClick={() => setSortBy('score')}
                className={`px-3 py-1 text-sm font-medium rounded-md ${
                  sortBy === 'score'
                    ? 'bg-survivor-100 text-survivor-700 border border-survivor-300'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                By Score
              </button>
              <button
                onClick={() => setSortBy('claiming')}
                className={`px-3 py-1 text-sm font-medium rounded-md ${
                  sortBy === 'claiming'
                    ? 'bg-survivor-100 text-survivor-700 border border-survivor-300'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                By Claiming Status
              </button>
            </div>
          </div>
        )}
      </div>
      
      {teams.length === 0 ? (
        <div className="text-center py-12">
          <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No teams</h3>
          <p className="mt-1 text-sm text-gray-500">
            No teams have joined this league for this season yet.
          </p>
          {showActions && onAddTeam && (
            <div className="mt-6">
              <button
                onClick={onAddTeam}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-survivor-600 hover:bg-survivor-700"
              >
                Add Team
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rank
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Team Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Score
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Draft Picks
                </th>
                {!hideMembers && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Members
                  </th>
                )}
                {!hideSubmissionStatus && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Submission Status
                  </th>
                )}
                {showActions && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredAndSortedTeams.map((team, index) => {
                const teamScore = getTeamScore(team);
                const rank = getTeamRank(team);
                const status = claimingStatus[team.id] || { isClaimed: false, claimCount: 0 };
                const isExpanded = expandedRows.has(team.id);
                const details = claimingDetails[team.id] || [];
                const isLoadingDetails = claimingDetailsLoading[team.id];
                
                return (
                  <React.Fragment key={team.id}>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {rank === 1 && <StarIcon className="h-5 w-5 text-yellow-500 mr-2" />}
                          <span className={`text-sm font-medium ${
                            rank === 1 ? 'text-yellow-600' : 
                            rank === 2 ? 'text-gray-600' : 
                            rank === 3 ? 'text-orange-600' : 'text-gray-900'
                          }`}>
                            #{rank}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {showTeamLinks ? (
                          <button
                            onClick={() => navigate(`/player/teams/${team.id}`)}
                            className="text-left text-sm font-medium text-survivor-600 hover:text-survivor-700 hover:underline"
                          >
                            {team.name}
                          </button>
                        ) : (
                          <div className="text-sm font-medium text-gray-900">
                            {team.name}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {teamScore} points
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {team.draftPicks && team.draftPicks.length > 0 ? (
                          <div className="space-y-1">
                            {team.draftPicks.map((pick, index) => {
                              const contestant = getContestantById(pick.contestantId, contestants);
                              const contestantName = contestant ? contestant.name : pick.contestantId || 'Unknown';
                              const isEliminated = isContestantEliminated(pick.contestantId);
                              
                              return (
                                <div key={index} className="flex items-center space-x-2">
                                  <span className="text-sm text-gray-900">{contestantName}</span>
                                  {isEliminated ? (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                      Voted Out
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                      Still Competing
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500">No picks</span>
                        )}
                      </td>
                      {!hideMembers && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          {status.isClaimed ? (
                            <div className="space-y-1">
                              <div className="flex items-center space-x-2">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  Claimed by {status.claimCount} {status.claimCount === 1 ? 'player' : 'players'}
                                </span>
                                <button
                                  onClick={() => toggleRowExpansion(team.id)}
                                  className="text-gray-400 hover:text-gray-600"
                                  title={isExpanded ? 'Hide details' : 'Show details'}
                                >
                                  {isExpanded ? (
                                    <ChevronUpIcon className="h-4 w-4" />
                                  ) : (
                                    <ChevronDownIcon className="h-4 w-4" />
                                  )}
                                </button>
                              </div>
                              <div className="text-sm text-gray-900">
                                {status.claimedBy && status.claimedBy.length > 0 ? (
                                  status.claimedBy.map((user, idx) => (
                                    <span key={user.id || idx}>
                                      {user.displayName || user.email || `User ${(user.id || '').substring(0, 8)}...`}
                                      {idx < status.claimedBy.length - 1 && ', '}
                                    </span>
                                  ))
                                ) : (
                                  'No members'
                                )}
                              </div>
                            </div>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              Unclaimed
                            </span>
                          )}
                        </td>
                      )}
                      {!hideSubmissionStatus && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          {(() => {
                            const submission = submissionStatus[team.id];
                            if (!submission || submission.totalWeeks === 0) {
                              return (
                                <span className="text-xs text-gray-500">No data</span>
                              );
                            }
                            const colorClass = getSubmissionStatusColor(submission.completionRate);
                            const label = getSubmissionStatusLabel(submission.completionRate);
                            return (
                              <div className="space-y-1">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
                                  {label} ({submission.completionRate}%)
                                </span>
                                <div className="text-xs text-gray-500">
                                  {submission.weeksWithPredictions}/{submission.totalWeeks} weeks
                                </div>
                                {submission.lastPredictionDate && (
                                  <div className="text-xs text-gray-400">
                                    Last: {formatDate(submission.lastPredictionDate)}
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                      )}
                      {showActions && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            {onViewTeam && (
                              <button
                                onClick={() => onViewTeam(team.id)}
                                className="text-survivor-600 hover:text-survivor-900"
                              >
                                View
                              </button>
                            )}
                            {onEditTeam && (
                              <button
                                onClick={() => onEditTeam(team.id)}
                                className="text-indigo-600 hover:text-indigo-900"
                              >
                                Edit
                              </button>
                            )}
                            {onRemoveTeam && (
                              <button
                                onClick={() => onRemoveTeam(team.id)}
                                className="text-red-600 hover:text-red-900"
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                    
                    {/* Expanded Details Row */}
                    {isExpanded && status.isClaimed && (
                      <tr>
                        <td colSpan={showActions ? (hideMembers && hideSubmissionStatus ? 4 : hideMembers || hideSubmissionStatus ? 5 : 6) : (hideMembers && hideSubmissionStatus ? 3 : hideMembers || hideSubmissionStatus ? 4 : 5)} className="px-6 py-4 bg-gray-50">
                          <div className="pl-8">
                            <h4 className="text-sm font-medium text-gray-900 mb-3">
                              Players who claimed this team:
                            </h4>
                            {isLoadingDetails ? (
                              <div className="flex items-center space-x-2 text-sm text-gray-500">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
                                <span>Loading details...</span>
                              </div>
                            ) : details.length > 0 ? (
                              <div className="space-y-2">
                                {details.map((user) => (
                                  <div key={user.id} className="flex items-center justify-between py-2 px-3 bg-white rounded border border-gray-200">
                                    <div>
                                      <div className="text-sm font-medium text-gray-900">
                                        {user.displayName}
                                      </div>
                                      <div className="text-xs text-gray-500">
                                        {user.email}
                                      </div>
                                    </div>
                                    {user.claimedAt && (
                                      <div className="text-xs text-gray-500">
                                        Claimed: {formatClaimDate(user.claimedAt)}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-gray-500">No details available</p>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
